import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { TokenService } from './token.service';

export type AuthModalType = 'login' | 'registration' | 'activateaccount' | 'forget-password' | 'change-password' | null;

@Injectable({
  providedIn: 'root'
})
export class AuthModalService {
  private modalStateSubject = new BehaviorSubject<AuthModalType>(null);
  public modalState$: Observable<AuthModalType> = this.modalStateSubject.asObservable();
  private returnUrl: string = '/';
  /** When true, the next NavigationEnd must not close the auth modal (used while clearing auth routes). */
  private preserveOnNextNavigation = false;
  /** Set while a logged-out user dismisses an auth modal; AuthGuard sends them to a public page instead of back to /login. */
  private authCancelled = false;
  readonly publicFallbackUrl = '/aboutus';

  constructor(private router: Router, private tokenService: TokenService) {}

  openModal(modalType: AuthModalType, returnUrl?: string): void {
    if (returnUrl) {
      this.returnUrl = returnUrl;
    } else {
      // Store current route if not provided
      this.returnUrl = window.location.pathname || '/';
    }
    document.body.classList.add('auth-modal-open');
    this.modalStateSubject.next(modalType);
  }

  /** Keep the modal open across the next router navigation (e.g. leaving /login for /). */
  preserveModalOnNextNavigation(): void {
    this.preserveOnNextNavigation = true;
  }

  /** Returns true once if a preserve was requested, then clears the flag. */
  consumePreserveOnNextNavigation(): boolean {
    if (this.preserveOnNextNavigation) {
      this.preserveOnNextNavigation = false;
      return true;
    }
    return false;
  }

  closeModal(): void {
    this.preserveOnNextNavigation = false;
    document.body.classList.remove('auth-modal-open');
    this.modalStateSubject.next(null);

    // If the auth form was opened via route (/login, /registration, …), leaving
    // the URL alone would drop hide-behind-auth-modal and show the form as a page.
    const path = (this.router.url || '/').split('?')[0];
    if (this.isAuthPath(path)) {
      const target = this.safeReturnUrl(this.returnUrl);
      if (!this.tokenService.getRefreshToken()) {
        this.authCancelled = true;
      }
      this.router
        .navigateByUrl(target, { replaceUrl: true })
        .finally(() => (this.authCancelled = false));
    }
  }

  /** Returns true once if a logged-out user just dismissed an auth modal, then clears the flag. */
  consumeAuthCancelled(): boolean {
    if (this.authCancelled) {
      this.authCancelled = false;
      return true;
    }
    return false;
  }

  private isAuthPath(path: string): boolean {
    return (
      path === '/login' ||
      path.startsWith('/login/') ||
      path === '/registration' ||
      path.startsWith('/registration/') ||
      path === '/activateaccount' ||
      path.startsWith('/activateaccount/') ||
      (path.includes('/forget-password') && !path.includes('/user-settings/')) ||
      (path.includes('/forgot-password') && !path.includes('/user-settings/')) ||
      (path.includes('/change-password') && !path.includes('/user-settings/'))
    );
  }

  private safeReturnUrl(url: string | null | undefined): string {
    const candidate = (url || '/').trim() || '/';
    const pathOnly = candidate.split('?')[0];
    if (this.isAuthPath(pathOnly) || pathOnly === '') {
      return '/';
    }
    return candidate;
  }

  getCurrentModal(): AuthModalType {
    return this.modalStateSubject.value;
  }

  getReturnUrl(): string {
    return this.returnUrl;
  }

  clearReturnUrl(): void {
    this.returnUrl = '/';
  }
}
